/**
 * Secure Storage Helper
 * Encrypts sensitive data before storing in localStorage
 * Provides an additional security layer against XSS attacks
 */

import CryptoJS from 'crypto-js';

// Generate a unique encryption key based on environment and browser fingerprint
const getEncryptionKey = () => {
    // Use environment variable as primary key
    const envKey = process.env.REACT_APP_ENCRYPTION_KEY || 'jaisalmer-trust-secure-2025';

    // Add browser fingerprint for additional security
    const browserFingerprint = navigator.userAgent + navigator.language;

    return CryptoJS.SHA256(envKey + browserFingerprint).toString();
};

const ENCRYPTION_KEY = getEncryptionKey();

/**
 * Encrypt data before storing
 */
export const encryptData = (data) => {
    try {
        const jsonString = JSON.stringify(data);
        return CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY).toString();
    } catch (error) {
        return null;
    }
};

/**
 * Decrypt data after retrieving
 */
export const decryptData = (encryptedData) => {
    try {
        if (!encryptedData) return null;

        const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
        const decryptedString = bytes.toString(CryptoJS.enc.Utf8);

        if (!decryptedString) return null;

        return JSON.parse(decryptedString);
    } catch (error) {
        return null;
    }
};

/**
 * Secure localStorage wrapper
 */
export const secureStorage = {
    /**
     * Set encrypted item in localStorage
     */
    setItem: (key, value) => {
        try {
            const encrypted = encryptData(value);
            if (encrypted) {
                localStorage.setItem(key, encrypted);
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    },

    /**
     * Get and decrypt item from localStorage
     */
    getItem: (key) => {
        try {
            const encrypted = localStorage.getItem(key);
            if (!encrypted) return null;

            return decryptData(encrypted);
        } catch (error) {
            return null;
        }
    },

    /**
     * Remove item from localStorage
     */
    removeItem: (key) => {
        try {
            localStorage.removeItem(key);
            return true;
        } catch (error) {
            return false;
        }
    },

    /**
     * Clear all items from localStorage
     */
    clear: () => {
        try {
            localStorage.clear();
            return true;
        } catch (error) {
            return false;
        }
    }
};

/**
 * Token-specific helpers
 */
export const tokenStorage = {
    /**
     * Set auth token with automatic expiration
     */
    setAuthToken: (tokens) => {
        const tokenData = {
            ...tokens,
            savedAt: Date.now(),
            expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
        };
        return secureStorage.setItem('authToken', tokenData);
    },

    /**
     * Get auth token and validate expiration
     */
    getAuthToken: () => {
        const tokenData = secureStorage.getItem('authToken');

        if (!tokenData) return null;

        // Check if token is expired
        if (tokenData.expiresAt && Date.now() > tokenData.expiresAt) {
            secureStorage.removeItem('authToken');
            return null;
        }

        return tokenData;
    },

    /**
     * Remove auth token
     */
    removeAuthToken: () => {
        return secureStorage.removeItem('authToken');
    },

    /**
     * Check if valid token exists
     */
    hasValidToken: () => {
        const token = tokenStorage.getAuthToken();
        return token && token.accessToken ? true : false;
    }
};
